import React, { useEffect, useState } from "react";
import ErrorComponent from "Components/ErrorComponent";
import { checkValidArray, handleServerResponse, saveEditForm, useSafeFormikContext } from "GenericForms/Helper";
import { Get } from "VesselMaster/vesselMaster.hooks";
import { useFormik } from "formik";
import apiGlobal from "global/api.global";
import { useQuery } from "react-query";
import {
    Row,
    Col,
    Label,
    Input,
    Button,
    Form,
    CardHeader,
    Card,
    CardBody,
    CardFooter,
} from "reactstrap";
import { queryKeyes } from "shared/queryKeys";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import env from "environment_system/env_system";
import { RootState } from "index";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentReportAction } from "Store/Generic/ReportingSlice";
import FormLoading from "Components/FormLoading";

interface PortActivitiesComponentType {
    activeAddTab: number;
    toggleAddTab: any;
    setErrorMessage: any;
    toggleTab: any;
    activeTab: number;
    editReportBool: boolean;
}

const PortActivitiesComponent = ({
    activeAddTab,
    toggleAddTab,
    setErrorMessage,
    toggleTab,
    activeTab,
    editReportBool
}: PortActivitiesComponentType) => {
    /** State variables start **/
    const { VesselID, VoyageID, ReportID, CurrentReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
    const portActivitiesCurrentReport = CurrentReport?.data?.deck_officer?.lfonds_port_activity_detail ?? null;
    const dispatch = useDispatch();
    const [refreshKey, setRefreshKey] = useState(0);
    const formikContext = useSafeFormikContext<any>();
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    /** State variables end **/

    /** Queries */
    /** Load Port Activities */
    const {
        data: PortActivities,
        isLoading: PortActivitiesLoading,
        isError: PortActivitiesError,
    } = useQuery(
        [queryKeyes.masters.PortActivities.key],
        async () => {
            return await Get(queryKeyes.masters.PortActivities.url());
        },
        { staleTime: Infinity }
    );
    /** Queries end */

    /** Assign values to initial object of Port Activities */
    const getInitialValues = () => {
        let obj: any[] = [];
        if (checkValidArray(editInitialValues?.deck_officer?.lfonds_port_activity_detail)) {
            return editInitialValues?.deck_officer?.lfonds_port_activity_detail;
        } else if (checkValidArray(portActivitiesCurrentReport)) {
            return portActivitiesCurrentReport;
        } else {
            if (
                obj !== undefined &&
                checkValidArray(PortActivities) &&
                obj <= PortActivities.length
            ) {
                PortActivities.map((activity: any) => {
                    obj.push({
                        is_port_activity: false,
                        comment: "",
                        port_activity_name: activity.port_activity_name,
                        port_activity: activity.id,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                    });
                    return "";
                });
            }
            return obj;
        }
    };

    /** Port Activities' formik object */
    const PortActivitiesFormik: any = useFormik({
        enableReinitialize: true,
        initialValues: {
            portActivities: getInitialValues(),
        },
        onSubmit: async (values: any, actions: any) => {
            if (env?.form_validation === false) {
                setErrorMessage(null)
                if (activeAddTab === 1) {
                    toggleTab(activeTab + 1);
                } else {
                    toggleAddTab(activeAddTab + 1);
                }
            }
            actions.setSubmitting(true);
            let responseArray: any = [];
            if (checkValidArray(portActivitiesCurrentReport)) {
                values.portActivities.forEach((val: any) => {
                    responseArray.push(apiGlobal.put(`/lfonds_port_activity_detail/${val.id}/`, val));
                })
            } else {
                responseArray.push(apiGlobal.post(`/lfonds_port_activity_detail/`, values.portActivities));
            }
            await handleServerResponse(responseArray).then(async (res: any) => {
                if (res === true) {
                    dispatch(getCurrentReportAction(VesselID, ReportID));
                    setRefreshKey(refreshKey + 1);
                    if (env?.form_validation === true) {
                        setErrorMessage(null)
                        if (activeAddTab === 1) {
                            toggleTab(activeTab + 1);
                        } else {
                            toggleAddTab(activeAddTab + 1);
                        }
                    }
                } else {
                    setErrorMessage(res)
                }
            })
            actions.setSubmitting(false);
        }
    });

    /** UseEffect */
    useEffect(() => {
        PortActivitiesFormik.initialValues.portActivities = getInitialValues();
        setRefreshKey(refreshKey + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portActivitiesCurrentReport]);
    useEffect(() => {
        if (checkValidArray(editInitialValues?.deck_officer?.lfonds_port_activity_detail)) {
            setEditValues({
                ...editInitialValues,
                deck_officer: {
                    ...editInitialValues?.deck_officer,
                    lfonds_port_activity_detail: PortActivitiesFormik?.values?.portActivities
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [PortActivitiesFormik?.values?.portActivities])
    /** UseEffect end */

    return (
        <Card className="p-0 mb-0">
            <CardHeader className="p-2">
                <div className="text-center">
                    <Row>
                        <Col>
                            <h4 className="page_title pos-start mb-0">Port Activities</h4>
                            <p className="card-title-desc pos-start">
                                All readings since last report
                            </p>
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {(PortActivitiesLoading || reportingLoading) && <FormLoading />}
            {(PortActivitiesError || reportingError) && <ErrorComponent message="Unable to load required data!" />}
            {!(PortActivitiesLoading || reportingLoading) && !(PortActivitiesError || reportingError) && (
                <Form autoComplete="off" onSubmit={PortActivitiesFormik?.handleSubmit} noValidate>
                    <CardBody className="px-2 py-0 mt-2">
                        <Row>
                            <Col sm={6}>
                                {PortActivitiesFormik?.values && PortActivitiesFormik?.values?.portActivities?.filter((_item: any, index: number) => index <= 6)?.map(
                                    (activity: any, index: number) => {
                                        return (
                                            <Row key={index} className="mb-1">
                                                <Col lg={4} className="mt-4">
                                                    <Label className="mb-0 mt-3" htmlFor={`port_activity_${index}`}>{activity.port_activity_name}</Label>
                                                </Col>
                                                <Col lg={2} className="mt-4">
                                                    <div className="square-switch sqswitch mt-3">
                                                        <Input
                                                            onFocus={(e) => e.target.select()}
                                                            type="checkbox"
                                                            autoFocus={true}
                                                            switch="none"
                                                            name={`portActivities.${index}.is_port_activity`}
                                                            id={`port_activity_${index}`}
                                                            onChange={(e: any) =>
                                                                PortActivitiesFormik?.handleChange(e)
                                                            }
                                                            defaultChecked={
                                                                PortActivitiesFormik?.values?.portActivities[index]
                                                                    .is_port_activity
                                                            }
                                                        />
                                                        <Label
                                                            htmlFor={`port_activity_${index}`}
                                                            data-on-label="Yes"
                                                            data-off-label="No"
                                                        />
                                                    </div>
                                                </Col>
                                                {PortActivitiesFormik?.values &&
                                                    PortActivitiesFormik?.values?.portActivities[index]
                                                        .is_port_activity === true && (
                                                        <Col lg={6} className="d-flex align-items-center">
                                                            <Input
                                                                onFocus={(e) => e.target.select()}
                                                                as="textarea"
                                                                name={`portActivities.${index}.comment`}
                                                                className="form-control mt-4"
                                                                rows={4}
                                                                onBlur={(e: any) => PortActivitiesFormik?.handleChange(e)}
                                                                defaultValue={PortActivitiesFormik?.values?.portActivities[index]?.comment}
                                                            />
                                                        </Col>
                                                    )}
                                            </Row>
                                        )
                                    }
                                )}
                            </Col>
                            <Col sm={6}>
                                {PortActivitiesFormik?.values && PortActivitiesFormik?.values?.portActivities?.filter((_item: any, index: number) => index > 6)?.map(
                                    (activity: any, index: number) => {
                                        return (
                                            <Row key={index} className="mb-1">
                                                <Col lg={4} className="mt-4">
                                                    <Label className="mb-0 mt-3" htmlFor={`port_activity_${PortActivitiesFormik?.values?.portActivities?.indexOf(activity)}`}>{activity.port_activity_name}</Label>
                                                </Col>
                                                <Col lg={2} className="mt-4">
                                                    <div className="square-switch sqswitch mt-3">
                                                        <Input
                                                            onFocus={(e) => e.target.select()}
                                                            type="checkbox"
                                                            switch="none"
                                                            name={`portActivities.${PortActivitiesFormik?.values?.portActivities?.indexOf(activity)}.is_port_activity`}
                                                            id={`port_activity_${PortActivitiesFormik?.values?.portActivities?.indexOf(activity)}`}
                                                            onChange={(e: any) =>
                                                                PortActivitiesFormik?.handleChange(e)
                                                            }
                                                            defaultChecked={
                                                                PortActivitiesFormik?.values?.portActivities[PortActivitiesFormik?.values?.portActivities?.indexOf(activity)]
                                                                    .is_port_activity
                                                            }
                                                        />
                                                        <Label
                                                            htmlFor={`port_activity_${PortActivitiesFormik?.values?.portActivities?.indexOf(activity)}`}
                                                            data-on-label="Yes"
                                                            data-off-label="No"
                                                        />
                                                    </div>
                                                </Col>
                                                {PortActivitiesFormik?.values &&
                                                    PortActivitiesFormik?.values?.portActivities[PortActivitiesFormik?.values?.portActivities?.indexOf(activity)]
                                                        .is_port_activity === true && (
                                                        <Col lg={6} className="d-flex align-items-center">
                                                            <Input
                                                                onFocus={(e) => e.target.select()}
                                                                as="textarea"
                                                                name={`portActivities.${PortActivitiesFormik?.values?.portActivities?.indexOf(activity)}.comment`}
                                                                className="form-control mt-4"
                                                                rows={4}
                                                                onBlur={(e: any) => PortActivitiesFormik?.handleChange(e)}
                                                                defaultValue={PortActivitiesFormik?.values?.portActivities[PortActivitiesFormik?.values?.portActivities?.indexOf(activity)]?.comment}
                                                            />
                                                        </Col>
                                                    )}
                                            </Row>
                                        )
                                    }
                                )}
                            </Col>
                        </Row>
                    </CardBody>
                    <CardFooter className="p-2 py-3">
                        <Row className="d-flex align-items-center flex-row-reverse">
                            <Col lg={10}>
                                <div className="d-flex flex-wrap gap-3 justify-content-end ">
                                    {/* {editReportBool && (
                                        <Button
                                            type="reset"
                                            color="danger"
                                            className="btn_size_cstm"
                                            onClick={() => {
                                                setEditValues({
                                                    ...editValues,
                                                    deck_officer: {
                                                        ...editValues?.deck_officer,
                                                        lfonds_port_activity_detail: editInitialValues?.deck_officer?.lfonds_port_activity_detail,
                                                    }
                                                });
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    )} */}
                                    <Button
                                        type={editReportBool ? "button" : "submit"}
                                        color="primary"
                                        className="btn_size_cstm"
                                        disabled={PortActivitiesFormik?.isSubmitting || editIsSubmitting}
                                        onClick={async () => {
                                            const validationErrors = await PortActivitiesFormik?.validateForm();
                                            Object.keys(PortActivitiesFormik?.values || {}).forEach((key: any) => {
                                                PortActivitiesFormik?.setFieldTouched(key, true);
                                            });
                                            if (editReportBool && Object.keys(validationErrors).length === 0) {
                                                await saveEditForm(editSubmit, toggleAddTab, activeAddTab);
                                            }
                                        }}
                                    >
                                        {(PortActivitiesFormik?.isSubmitting || editIsSubmitting) && (
                                            <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                        )}
                                        Next <i className="bx bx-chevron-right ms-1" />
                                    </Button>
                                </div>
                            </Col>
                            <Col lg={2}>
                                <Button
                                    type="button"
                                    color="primary"
                                    className="btn_size_cstm"
                                    onClick={() => {
                                        PortActivitiesFormik?.setErrors({});
                                        setErrorMessage(null);
                                        if (activeAddTab === 1) {
                                            toggleTab(activeTab - 1);
                                        } else {
                                            toggleAddTab(activeAddTab - 1);
                                        }
                                    }}
                                >
                                    <i className="bx bx-chevron-left me-1" /> Previous
                                </Button>
                            </Col>
                        </Row>
                    </CardFooter>
                    <FormValuesDebug
                        values={[PortActivitiesFormik?.values, PortActivitiesFormik?.errors, PortActivitiesFormik.initialValues]}
                    />
                </Form >
            )}
        </Card >
    );
};

export default PortActivitiesComponent;
